/*
 * <sale-bogo-cart> keeps the cart right for sale mode's Buy 1 Get 1 toner.
 *
 * snippets/sale-bogo-cart.liquid renders it inside the cart drawer's
 * <cart-drawer-items> and the cart page's <cart-items>, with every decision
 * already made server-side in data-* attributes. Each cart re-render replaces
 * it, so connectedCallback runs again against the new cart. It does, in order:
 *
 *   1. remove lines checkout would charge for: a second free toner (one per
 *      order), a `_bogo_free` line that is not the free variant, and the free
 *      toner once nothing paid in the cart qualifies;
 *   2. pin the free toner to quantity 1 (the Cart Transform only zeroes qty 1);
 *   3. retire a decline once the cart stops qualifying;
 *   4. add the free toner when the cart qualifies and has none, unless the
 *      customer removed it from this cart.
 *
 * Most adds never reach step 4: product-form.js sends the free toner in the
 * same request as the product. When step 4 does run with the drawer open, the
 * drawer shows its loading row until the toner lands (showAddingRow). Step 4 covers everything else that can put a
 * qualifying product in the cart (drawer upsells, the cart page, a cart that
 * changed in another tab).
 *
 * Each action changes the condition it acted on, so the check after its
 * re-render finds nothing to do: no loop. Step 4 is also guarded in case
 * something else undoes it. If the cart that comes back from an add holds no
 * valid free toner, adding stops for the session. That is what an Intelligems
 * price test did before its copy of the toner counted as the free variant
 * (see sale-bogo-cart.liquid): it swapped the add for the copy, step 1
 * removed the copy and step 4 added it back, over a thousand times. There
 * are also at most three adds per page.
 *
 * Shared with the free gift: both elements act on the same drawer re-render.
 * The check runs a task later (setTimeout 0), after the gift's own
 * connectedCallback, and skips while the gift has a request in flight; the
 * gift's own re-render then re-runs it. While this element has a request in
 * flight it holds the gift's flag the same way, and releases it before
 * re-rendering so the gift's next check can act.
 */
if (!customElements.get('sale-bogo-cart')) {
  class SaleBogoCart extends HTMLElement {
    connectedCallback() {
      if (!SaleBogoCart.removeBound) {
        SaleBogoCart.removeBound = true;
        document.addEventListener('click', SaleBogoCart.onRemoveClick, true);
      }
      setTimeout(() => this.evaluate(), 0);
    }

    // The customer removing the free toner is a decline: record it before
    // Dawn's own removal runs, or the re-render adds the toner straight back.
    // Capture phase, so this runs before CartRemoveButton's handler, then hands
    // the delete back to Dawn so the spinner and re-render are the native ones.
    // Same shape as the gift's decline in cart-free-gift.js.
    static onRemoveClick(event) {
      const target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      const removeBtn = target.closest('cart-remove-button');
      if (!removeBtn || !removeBtn.closest('.cart-item--bogo-free')) return;

      const cartItems = removeBtn.closest('cart-drawer-items') || removeBtn.closest('cart-items');
      const line = removeBtn.dataset.index;
      if (!cartItems || !line) return;

      event.preventDefault();
      event.stopPropagation();
      cartItems.enableLoading(line);
      SaleBogoCart.post('update', { attributes: { _bogo_declined: 'true' } })
        .catch(() => {})
        .then(() => cartItems.updateQuantity(line, 0, event));
    }

    static routes() {
      const r = window.routes || {};
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return {
        add: r.cart_add_url || root + 'cart/add.js',
        update: r.cart_update_url || root + 'cart/update.js',
      };
    }

    // Accept: application/json is required: routes.* have no .js suffix, and
    // without the header Shopify answers with the HTML cart page.
    static post(kind, body) {
      return fetch(SaleBogoCart.routes()[kind], {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      }).then((r) => r.json().then((data) => cartResponse(r.status, data)));
    }

    giftBusy() {
      const Gift = customElements.get('free-gift-progress');
      return !!(Gift && Gift.busy);
    }

    setGiftBusy(value) {
      const Gift = customElements.get('free-gift-progress');
      if (Gift) Gift.busy = value;
    }

    evaluate() {
      if (!this.isConnected || SaleBogoCart.busy || this.giftBusy()) return;
      // On the cart page the drawer is rendered too; only the page's copy acts.
      if (this.dataset.context === 'drawer' && document.querySelector('sale-bogo-cart[data-context="page"]')) return;

      const d = this.dataset;
      const running = d.running === 'true';
      const qualified = d.qualified === 'true';
      const freeKey = d.freeKey || '';
      const freeQty = parseInt(d.freeQty, 10) || 0;
      const declined = d.declined === 'true';
      const reward = parseInt(d.reward, 10) || 0;

      // The first check after one of our adds: if it produced no valid free
      // line, stop adding for this session (see the header).
      if (SaleBogoCart.addPending) {
        SaleBogoCart.addPending = false;
        if (!freeKey) SaleBogoCart.stopAdding();
      }

      const updates = {};
      (d.extraKeys || '').split(',').forEach((key) => {
        if (key) updates[key] = 0;
      });
      if (freeKey && !qualified) updates[freeKey] = 0;
      else if (freeKey && freeQty > 1) updates[freeKey] = 1;

      if (Object.keys(updates).length) {
        this.mutate('update', { updates: updates });
        return;
      }

      if (declined && !qualified) {
        // A decline belongs to the cart that qualified when it was made; cart
        // attributes outlive their lines. Nothing renders differently, so no
        // re-render; the dataset is updated in place because product-form.js
        // reads it on the next add.
        this.dataset.declined = 'false';
        SaleBogoCart.post('update', { attributes: { _bogo_declined: '' } }).catch(() => {});
        return;
      }

      // The <html> class is the reveal switch (layout/theme.liquid): set
      // server-side while the sale runs sitewide, and the hook a future URL or
      // UTM trigger would set instead.
      const revealed = document.documentElement.classList.contains('sale-bogo-on');
      if (running && revealed && qualified && !freeKey && !declined && reward && SaleBogoCart.mayAdd()) {
        SaleBogoCart.addCount += 1;
        SaleBogoCart.addPending = true;
        this.mutate('add', {
          items: [{ id: reward, quantity: 1, properties: { _bogo_free: 'true' } }],
        });
      }
    }

    static mayAdd() {
      if (SaleBogoCart.addStopped || SaleBogoCart.addCount >= 3) return false;
      try {
        if (window.sessionStorage.getItem(SaleBogoCart.STOP_KEY) === '1') {
          SaleBogoCart.addStopped = true;
          return false;
        }
      } catch (e) {}
      return true;
    }

    static stopAdding() {
      SaleBogoCart.addStopped = true;
      try {
        window.sessionStorage.setItem(SaleBogoCart.STOP_KEY, '1');
      } catch (e) {}
    }

    mutate(kind, body) {
      SaleBogoCart.busy = true;
      this.setGiftBusy(true);

      const surface = this.surface();
      // Adding the free toner is a second round trip after the product's own
      // add, so while the drawer is open show the loading row a customer's add
      // gets, instead of a gap before the toner appears.
      const drawer = document.querySelector('cart-drawer');
      const showedRow =
        kind === 'add' && this.dataset.context !== 'page' && drawer && typeof drawer.showAddingRow === 'function'
          ? drawer.showAddingRow()
          : false;
      body.sections = surface.sections.map((s) => s.section);
      body.sections_url = window.location.pathname;

      SaleBogoCart.post(kind, body)
        .then((res) => {
          if (res && res.status) throw new Error(res.description || res.message || 'Cart update failed');
          SaleBogoCart.busy = false;
          this.setGiftBusy(false);
          surface.render(res);
        })
        .catch((err) => {
          // No re-render on failure: the next genuine cart change re-checks.
          console.error('Buy 1 Get 1 cart update:', err);
          if (kind === 'add') SaleBogoCart.addPending = false;
          if (showedRow) drawer.clearLoading();
          SaleBogoCart.busy = false;
          this.setGiftBusy(false);
        });
    }

    // Which sections to ask for, and how to put them on the page.
    surface() {
      if (this.dataset.context === 'page') {
        const cartItems = this.closest('cart-items');
        const sections = cartItems ? cartItems.getSectionsToRender() : [];
        return {
          sections: sections,
          render: (res) => {
            if (!res.sections || !cartItems) {
              window.location.reload();
              return;
            }
            // The same swap CartItems.updateQuantity does.
            sections.forEach((section) => {
              const html = res.sections[section.section];
              const host = document.getElementById(section.id);
              if (!html || !host) return;
              const target = host.querySelector(section.selector) || host;
              target.innerHTML = cartItems.getSectionInnerHTML(html, section.selector);
            });
          },
        };
      }

      const drawer = document.querySelector('cart-drawer');
      const sections = drawer ? drawer.getSectionsToRender().map((s) => ({ section: s.id })) : [];
      return {
        sections: sections,
        render: (res) => {
          if (!drawer) return;
          if (res.sections && res.sections['cart-drawer']) {
            // keepClosed: a check that runs on page load must not pop the
            // drawer open.
            drawer.renderContents(res, { keepClosed: true });
            return;
          }
          // /cart/add.js with items[] can omit sections; fetch them instead.
          const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          fetch(root + '?sections=' + sections.map((s) => s.section).join(','))
            .then((r) => r.json())
            .then((fetched) => drawer.renderContents({ sections: fetched }, { keepClosed: true }))
            .catch((err) => console.error('Buy 1 Get 1 cart refresh:', err));
        },
      };
    }
  }

  SaleBogoCart.busy = false;
  SaleBogoCart.removeBound = false;
  SaleBogoCart.addCount = 0;
  SaleBogoCart.addPending = false;
  SaleBogoCart.addStopped = false;
  SaleBogoCart.STOP_KEY = 'jiyu-sale-bogo-add-stopped';
  customElements.define('sale-bogo-cart', SaleBogoCart);
}
